import { ChevronRight, LayoutGrid } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from '../../../lib/i18n';
import { useBasePath } from '../../../lib/use-base-path';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useAllWorkspaceApps, useWorkspaceApps } from '../../../lib/app-shares';
import { useWorkspaces } from '../../../lib/workspaces';
import { countNewSince } from '../../../lib/summary-freshness';
import { useNewSince } from '../../../lib/use-last-visited';
import { Badge, Card, Typography } from '../ui';
import { AgentEntryRow } from './AgentEntryRow';
import styles from './FilesSummaryCard.module.css';

// The Apps tile — the fourth alongside Tasks · Routines · Files, opening the Apps screen. Built to the
// same 249×72 shape as the three beside it (Figma `15378:117541`): the grid glyph 4 from "Apps", the
// OUTLINED "N New" pill beside it, one 12px stat line, and the centred drill-in chevron.
//
// Scoped like the Files tile: account-wide by default, or one Space when given `workspaceId` +
// `workspaceSlug`, and it opens whichever screen it counted.
//
// **Cost, stated plainly:** the account-wide count is a fan-out — one read per Space — where the Files
// tile gets a single account-wide request. There is no cross-Space apps endpoint; `getWorkspaceArtifacts`
// is per-Space. The queries are shared through React Query with the `/apps` screen this opens (so the
// screen is free once the tile has paid) and the tile never blocks paint: it renders its title and
// chevron immediately and fills the count in. If Spaces-per-account grows enough for the fan-out to
// bite, the fix is an aggregate endpoint, not a cache tweak here.
export function AppsSummaryCard({
  pageName = 'agents',
  entryRow = false,
  workspaceId,
  workspaceSlug,
}: {
  pageName?: string;
  entryRow?: boolean;
  workspaceId?: string;
  workspaceSlug?: string;
} = {}) {
  const { t } = useTranslation();
  const basePath = useBasePath();
  const { logPageClickBi } = useGenericBi();

  const workspacesState = useWorkspaces();
  // Only fan out for the account-wide tile; a Space's tile reads its own Space and nothing else.
  const allIds = useMemo(
    () => (workspaceId || workspacesState.status !== 'ready' ? [] : workspacesState.workspaces.map((w) => w.id).filter(Boolean)),
    [workspaceId, workspacesState],
  );
  const all = useAllWorkspaceApps(allIds);
  const one = useWorkspaceApps(workspaceId);

  const apps = workspaceId ? (one.status === 'ready' ? one.apps : []) : all.status === 'ready' ? all.apps : [];
  const spaces = workspaceId ? 0 : new Set(apps.map((a) => a.workspaceId).filter(Boolean)).size;

  // "N New" apps since the reader last opened the Apps screen — the same new-since-last-visit mechanism
  // the Files tile uses (`useNewSince` + `countNewSince` over `createdDate`). Captured at mount so the
  // badge doesn't vanish the instant they come back from `/apps`.
  const since = useNewSince('apps');
  const newCount = countNewSince(apps, since ?? Number.POSITIVE_INFINITY);

  const open = () => logPageClickBi({ page_name: pageName, button_name: 'open_apps' });
  const to = workspaceSlug ? `${basePath}/workspace/${encodeURIComponent(workspaceSlug)}/apps` : `${basePath}/apps`;
  // Spread as a record, the way the Files tile does it: Card's props do not declare `href`, even though
  // it forwards them to the `a` it renders.
  const href = { href: to } as Record<string, string>;

  // Until the read lands — and for an account with no apps — the prose blurb, not "0 apps · 0 spaces",
  // which states an emptiness the reader did not ask about. Same rule as the Files tile.
  const subtitle =
    apps.length === 0
      ? undefined
      : workspaceId
      ? t('apps.stats.workspace', { apps: apps.length })
      : t('apps.stats.account', { apps: apps.length, spaces });

  // Mobile entry row (Figma 15482:103484): just the title and the OUTLINED "N New" pill, then the
  // drill-in chevron — no stats subtitle (unlike the Files card beside it). Mirrors native's Apps
  // `entryRowCard`.
  if (entryRow) {
    return (
      <AgentEntryRow
        title={t('apps.section.title')}
        href={to}
        onClick={open}
        testId="agentsApps"
        trailing={
          newCount > 0 ? (
            <Badge variant="agent-profession" label={t('summary.newBadge', { count: newCount })} />
          ) : undefined
        }
      />
    );
  }

  return (
    <Card component="a" interactive size="sm" className={styles.card} {...href} onClick={open}>
      <div className={styles.body}>
        <span className={styles.title}>
          <LayoutGrid size={16} aria-hidden />
          <Typography variant="h4" color="strong" component="span" className={styles.titleText}>
            {t('apps.section.title')}
          </Typography>
          {/* The desktop tile used to be the one of the four with no freshness badge, though it had
              already counted `newCount` for its mobile row — the same gap on one viewport only. */}
          {newCount > 0 && (
            <Badge variant="agent-profession" className={styles.new} label={t('summary.newBadge', { count: newCount })} />
          )}
        </span>
        {/* One line, ellipsised — see the Files tile: the `apps.page.description` fallback is a
            sentence, and a wrapped tile is 90 tall where the frame draws 72. */}
        <Typography variant="p3" color="secondary" noWrap>
          {subtitle ?? t('apps.page.description')}
        </Typography>
      </div>
      <ChevronRight size={16} className={`${styles.chevron} dir-flip`} aria-hidden />
    </Card>
  );
}
